import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Paperclip, X, FileText, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

// Uploads any file (PDF, docs, etc.) to object storage and tracks metadata.
export default function AttachmentUploader({ attachments, setAttachments, testid = "attachment-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    setUploading(true);
    for (const f of files) {
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 50MB and was skipped`);
        continue;
      }
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeAt = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div data-testid={testid}>
      <div className="space-y-2">
        {attachments.map((a, i) => (
          <div key={a.id || i} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm">
            <FileText className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="truncate text-slate-700 flex-1">{a.name}</span>
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="text-slate-300 hover:text-red-500"
              aria-label="Remove attachment"
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors"
        data-testid={`${testid}-add`}
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        Attach files (PDF, video, docs…)
      </button>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} data-testid={`${testid}-input`} />
    </div>
  );
}

export function AttachmentList({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  const token = localStorage.getItem("token");
  const base = `${process.env.REACT_APP_BACKEND_URL}/api`;
  return (
    <div className="mt-3 space-y-2">
      {attachments.map((a, i) => (
        <a
          key={a.id || i}
          href={`${base}/files/${a.id}?auth=${token}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
          data-testid={`attachment-link-${i}`}
        >
          <FileText className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="truncate text-slate-700 flex-1">{a.name}</span>
          <Download className="h-4 w-4 text-slate-400 shrink-0" />
        </a>
      ))}
    </div>
  );
}
